import {Baseline, PlayerSession, Team} from '../interfaces';
import {DateTime} from 'luxon';
import {PlayerSessionFilterForm} from '../components/shared/PlayerSessionFilter';
const isPlayerSession = (
  variable: Baseline | PlayerSession | number,
): variable is PlayerSession => {
  return (variable as PlayerSession).startTime !== undefined;
};

const isAllPlayerSessions = (
  array: (Baseline | PlayerSession)[],
): array is PlayerSession[] => {
  return array.every(isPlayerSession);
};

const getIntensityText = (value?: number) => {
  if (value === 1) {
    return 'Low';
  } else if (value === 2) {
    return 'Avg';
  } else if (value === 3) {
    return 'High';
  }
  return '';
};

const getBaselinesInDateRange = (baselines: Baseline[], daysAgo: number) => {
  const endOfCurrentDay = DateTime.local().endOf('day');
  const dataInRange = baselines.filter(baseline => {
    const relevantDate = baseline.lastSessionDate;
    if (!relevantDate) {
      return false;
    }
    const dateDiff = DateTime.fromISO(relevantDate?.toString()).diff(
      endOfCurrentDay,
      'days',
    ).days;
    return dateDiff * -1 < daysAgo;
  });
  return dataInRange;
};

const getSessionsInDateRange = (
  playerSessions: PlayerSession[],
  daysAgo: number,
) => {
  const endOfCurrentDay = DateTime.local().endOf('day');
  const dataInRange = playerSessions.filter(playerSession => {
    const relevantDate = playerSession.startTime;
    if (!relevantDate) {
      return false;
    }
    const dateDiff = DateTime.fromISO(relevantDate?.toString()).diff(
      endOfCurrentDay,
      'days',
    ).days;
    return dateDiff * -1 < daysAgo;
  });
  return dataInRange;
};

const filterPlayerSessions = (
  playerSessions: PlayerSession[],
  filter: PlayerSessionFilterForm,
) => {
  const sessionBeginTime = filter.dateRange?.startDate?.valueOf() ?? null;
  const sessionEndDate = filter.dateRange?.endDate
    ? new Date(filter.dateRange.endDate)
    : null;
  const sessionEndTime = sessionEndDate
    ? sessionEndDate.setDate(sessionEndDate.getDate() + 1)
    : null;
  return (playerSessions || []).filter(ps => {
    if (!ps.startTime) {
      return false;
    }
    const startTime = new Date(ps.startTime).valueOf();

    if (sessionBeginTime !== null && startTime < sessionBeginTime) {
      return false;
    }
    if (sessionEndTime !== null && startTime > sessionEndTime) {
      return false;
    }

    const {teamIds, sessionTypes, dataStatus, sessionMedia} = filter;

    // if not every value is false (meaning unselected)
    if (!Object.values(teamIds).every(val => !val)) {
      // if its an individual session and teamIds.individual is false
      // then the filter should return false
      if (!ps.teamSession && !teamIds?.individual) {
        return false;
      }
      // if its a teamSession and teamIds.[teamId] is false
      // then the filter should return false
      if (ps.teamSession?.team?.id && !teamIds[ps.teamSession?.team?.id]) {
        return false;
      }
    }

    if (dataStatus.pending && !dataStatus.processed) {
      // If no player sessions are still processing, return false
      if (ps.hasPlayerData) {
        return false;
      }
    }

    if (dataStatus.processed && !dataStatus.pending) {
      // If player sessions are still processing, return false
      if (!ps.hasPlayerData) {
        return false;
      }
    }

    if (sessionMedia?.hasVideo && !ps.hasVideo) {
      return false;
    }

    // if not every sessionType value is false (meaning unselected)
    if (!Object.values(sessionTypes).every(val => !val)) {
      // then make sure the session type is in the validSessionTypes array
      const validSessionTypes: string[] = (
        Object.keys(sessionTypes) as Array<keyof typeof sessionTypes>
      ).filter(key => sessionTypes[key]);
      if (!validSessionTypes.includes(ps.type as string)) {
        return false;
      }
    }

    return true;
  });
};

const getUniquePlayerSessionTeams = (playerSessions: PlayerSession[]) => {
  // First get unique team IDs
  const uniqueTeamIds = [
    ...new Set(
      (playerSessions ?? [])
        .map(ps => ps?.teamSession?.team?.id)
        .filter(id => id),
    ),
  ] as number[];

  // Then map back to the full team objects
  return (playerSessions ?? [])
    .map(ps => ps?.teamSession?.team)
    .filter(team => team)
    .filter(
      (team, index, self) =>
        uniqueTeamIds.includes(team?.id as number) &&
        (self as Team[]).findIndex(t => t?.id === team?.id) === index,
    ) as Team[];
};

export {
  getIntensityText,
  getSessionsInDateRange,
  isPlayerSession,
  getBaselinesInDateRange,
  isAllPlayerSessions,
  filterPlayerSessions,
  getUniquePlayerSessionTeams,
};
